const { REACT_APP_NAME } = process.env;

import {
    Box,
    Typography,
    Card,
    CardContent,
    Avatar,
    Button,
    Grid,
    IconButton,
    useTheme,
    useMediaQuery,
    Skeleton,
    TextField,
    InputAdornment
} from '@mui/material';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageContainer from 'src/components/container/PageContainer';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { usePostBodyWithJwtMutation } from 'src/redux/api/cabinet';
import { palette } from 'src/theme/style/palette';
import { IconSearch } from '@tabler/icons-react';

interface Level {
    name: string;
    level: number;
    status: boolean;
    is_current: boolean;
    currency?: string;
}

interface Program {
    id: number;
    name: string;
    balance_program?: string;
    balance_program_name?: string;
    status: boolean;
    currency?: 'usdt' | 'btc';
    levels: Level[];
}

interface Position {
    link?: string;
    avatar?: string;
    login?: string;
    kurator?: string;
}

interface ServerCard {
    positions: (Position | false)[];
    name_program: string;
    name_level: string;
}

interface TransformedCard {
    id: number;
    title: string;
    avatar: string;
    username: string;
    timestamp: string;
    positions: (Position | false)[];
    link: string;
    kurator: string;
}

type CurrencyType = 'usdt' | 'btc';

export const MarketingAnaliticsPage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [searchParams, setSearchParams] = useSearchParams();

    const [selectedCurrency, setSelectedCurrency] = useState<CurrencyType>('usdt');
    const [selectedProgramTab, setSelectedProgramTab] = useState(0);
    const [selectedLevelTab, setSelectedLevelTab] = useState(0);
    const [programs, setPrograms] = useState<Program[]>([]);
    const [serverCards, setServerCards] = useState<ServerCard[]>([]);
    const [activeProgram, setActiveProgram] = useState<Program | null>(null);
    const [valueLogin, setValueLogin] = useState("");
    const [isInitialized, setIsInitialized] = useState(false);
    const [serverMessage, setServerMessage] = useState("Нет данных для отображения");

    const jwt = localStorage.getItem('jwt') || '';
    const [postBodyWithJwt, { isLoading, error }] = usePostBodyWithJwtMutation();

    // Фильтруем программы по выбранной валюте (если currency не указан, считаем его usdt)
    const filteredPrograms = useMemo(() => {
        return programs.filter(program => (program.currency?.toLowerCase() || 'usdt') === selectedCurrency);
    }, [programs, selectedCurrency]);

    const updateURLParams = useCallback((
        currency: CurrencyType,
        programIndex: number,
        levelIndex: number,
        login: string
    ) => {
        const params: Record<string, string> = { currency };

        if (filteredPrograms[programIndex]) {
            params.program = filteredPrograms[programIndex].id.toString();
        }
        if (filteredPrograms[programIndex]?.levels?.[levelIndex]) {
            params.level = filteredPrograms[programIndex].levels[levelIndex].level.toString();
        }
        if (login) {
            params.login = login;
        }
        setSearchParams(params);
    }, [filteredPrograms, setSearchParams]);

    const fetchServerData = useCallback(async (programId: number, level: number, body = {}) => {
        try {
            const response = await postBodyWithJwt({
                route: 'marketing-analytics',
                body: {
                    program: programId,
                    level: level,
                    login: valueLogin,
                    ...body
                },
                jwt
            }).unwrap();
            if (response.data) {
                setServerCards(response.data);
                setServerMessage(response?.message || "Нет данных для отображения");
            }
        } catch (error) {
            console.error('Failed to fetch server data:', error);
        }
    }, [postBodyWithJwt, jwt, valueLogin]);

    const handleCurrencyChange = useCallback((newCurrency: CurrencyType) => {
        setSelectedCurrency(newCurrency);
        setSelectedProgramTab(0);
        setSelectedLevelTab(0);

        const currencyPrograms = programs.filter(p => (p.currency || 'usdt') === newCurrency);

        if (currencyPrograms.length > 0) {
            const firstProgram = currencyPrograms[0];
            setActiveProgram(firstProgram);

            if (firstProgram.levels && firstProgram.levels.length > 0) {
                fetchServerData(firstProgram.id, firstProgram.levels[0].level);
                updateURLParams(newCurrency, 0, 0, valueLogin);
            }
        }
    }, [programs, valueLogin, updateURLParams, fetchServerData]);

    const handleProgramTabChange = useCallback((event: any, newValue: number, search = false) => {
        setSelectedProgramTab(newValue);
        if (!search) {
            setSelectedLevelTab(0);
        }

        const selectedProgram = filteredPrograms[newValue];
        setActiveProgram(selectedProgram);

        if (selectedProgram?.levels && selectedProgram.levels.length > 0) {
            const levelIndex = search ? selectedLevelTab : 0;
            fetchServerData(selectedProgram.id, selectedProgram.levels[levelIndex].level);
            updateURLParams(selectedCurrency, newValue, levelIndex, valueLogin);
        }
    }, [filteredPrograms, selectedLevelTab, selectedCurrency, valueLogin, updateURLParams, fetchServerData]);

    const handleLevelTabChange = useCallback((event: any, newValue: number) => {
        setSelectedLevelTab(newValue);

        const selectedProgram = filteredPrograms[selectedProgramTab];
        if (selectedProgram?.levels?.[newValue]) {
            fetchServerData(selectedProgram.id, selectedProgram.levels[newValue].level);
            updateURLParams(selectedCurrency, selectedProgramTab, newValue, valueLogin);
        }
    }, [filteredPrograms, selectedProgramTab, selectedCurrency, valueLogin, updateURLParams, fetchServerData]);

    const fetchInitialData = useCallback(async (body = {}) => {
        try {
            const response = await postBodyWithJwt({
                route: 'marketing-analytics?program=7&level=6',
                body,
                jwt
            }).unwrap();

            setServerMessage(response?.message || "Нет данных для отображения");

            if (response.programs) {
                // Нормализуем программы: если currency не указан, ставим 'usdt'
                const normalizedPrograms = response.programs.map((program: Program) => ({
                    ...program,
                    currency: program.currency?.toLowerCase() || 'usdt'
                }));

                setPrograms(normalizedPrograms);
                setServerCards(response.data || []);

                // Читаем currency из URL параметров
                const urlCurrency = (searchParams.get('currency') as CurrencyType);
                const validCurrency: CurrencyType = (urlCurrency === 'btc' || urlCurrency === 'usdt') ? urlCurrency : 'usdt';

                const urlProgramId = searchParams.get('program');
                const urlLevel = searchParams.get('level');
                const urlLogin = searchParams.get('login');

                if (urlLogin) {
                    setValueLogin(urlLogin);
                }

                setSelectedCurrency(validCurrency);

                const currencyPrograms = normalizedPrograms.filter((p: Program) => p.currency === validCurrency);

                let defaultProgramIndex = 0;
                let defaultLevelIndex = 0;

                if (urlProgramId && currencyPrograms.length > 0) {
                    const programIndex = currencyPrograms.findIndex((p: Program) => p.id === parseInt(urlProgramId));
                    if (programIndex !== -1) {
                        defaultProgramIndex = programIndex;
                    }
                }

                if (defaultProgramIndex === 0 && !urlProgramId && currencyPrograms.length > 0) {
                    const activeProgramIndex = currencyPrograms.findIndex((p: Program) => p.status === true);
                    defaultProgramIndex = activeProgramIndex !== -1 ? activeProgramIndex : 0;
                }

                setSelectedProgramTab(defaultProgramIndex);

                const activeProgram = currencyPrograms[defaultProgramIndex];
                setActiveProgram(activeProgram);

                if (activeProgram?.levels) {
                    if (urlLevel) {
                        const levelIndex = activeProgram.levels.findIndex((l: Level) => l.level === parseFloat(urlLevel));
                        if (levelIndex !== -1) {
                            defaultLevelIndex = levelIndex;
                        }
                    }

                    if (defaultLevelIndex === 0 && !urlLevel) {
                        const activeLevelIndex = activeProgram.levels.findIndex((l: Level) => l.is_current === true);
                        defaultLevelIndex = activeLevelIndex !== -1 ? activeLevelIndex : 0;
                    }

                    setSelectedLevelTab(defaultLevelIndex);

                    if (urlProgramId || urlLevel || urlLogin) {
                        fetchServerData(activeProgram.id, activeProgram.levels[defaultLevelIndex].level, { login: urlLogin || '' });
                    }
                }

                // Всегда обновляем URL с правильными параметрами
                updateURLParams(validCurrency, defaultProgramIndex, defaultLevelIndex, urlLogin || '');

                setIsInitialized(true);
            }
        } catch (error) {
            console.error('Failed to fetch initial data:', error);
            setIsInitialized(true);
        }
    }, [jwt, searchParams, updateURLParams, postBodyWithJwt, fetchServerData]);

    useEffect(() => {
        if (jwt && !isInitialized) {
            fetchInitialData();
        }
    }, [jwt, isInitialized, fetchInitialData]);

    const handleSubmit = useCallback(() => {
        if (valueLogin.trim()) {
            handleProgramTabChange(null, selectedProgramTab, true);
            updateURLParams(selectedCurrency, selectedProgramTab, selectedLevelTab, valueLogin.trim());
        }
    }, [valueLogin, selectedProgramTab, selectedCurrency, selectedLevelTab, handleProgramTabChange, updateURLParams]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleSubmit();
        }
    }, [handleSubmit]);

    const transformedServerCards: TransformedCard[] = useMemo(() => {
        return serverCards.map((item, index) => {
            const centerUser = item?.positions?.[0];
            const positions = item?.positions || [];

            return {
                id: index + 1,
                title: `${item.name_program} ${item.name_level}`,
                avatar: (centerUser && typeof centerUser !== 'boolean') ? centerUser.avatar || '' : '',
                username: (centerUser && typeof centerUser !== 'boolean') ? centerUser.login || '' : '',
                timestamp: '',
                positions: positions,
                link: (centerUser && typeof centerUser !== 'boolean') ? centerUser.link || '' : '',
                kurator: (centerUser && typeof centerUser !== 'boolean') ? centerUser.kurator || '' : ''
            };
        });
    }, [serverCards]);

    const ServerCard = ({ server }: { server: TransformedCard }) => (
        <Card
            sx={{
                bgcolor: 'rgba(74, 58, 132, 0.2)',
                border: '1px solid rgba(138, 118, 200, 0.3)',
                borderRadius: 2,
                minHeight: 280,
                width: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
                <Typography
                    variant="h6"
                    sx={{
                        color: '#993399',
                        textAlign: 'center',
                        mb: 2,
                        fontWeight: 500,
                        fontSize: isMobile ? '1rem' : '1.1rem'
                    }}
                >
                    {server.title}
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                    <Avatar
                        src={server.avatar}
                        sx={{
                            width: isMobile ? 50 : 60,
                            height: isMobile ? 50 : 60,
                            mb: 1,
                            bgcolor: server.username ? 'primary.main' : 'grey.700'
                        }}
                    >
                        {!server.username && '?'}
                    </Avatar>

                    {server.username && (
                        <>
                            <Typography variant="body2" sx={{ color: palette.blue, fontWeight: 500, fontSize: 20 }}>
                                {server.username}
                            </Typography>
                            {server.kurator && (
                                <Typography variant="caption" sx={{ fontSize: 18, color: palette.blue }}>
                                    ({server.kurator})
                                </Typography>
                            )}
                        </>
                    )}
                </Box>

                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    mt: 'auto',
                    mb: 2,
                    gap: 1
                }}>
                    {server.positions.slice(1).map((position, index) => (
                        <Box key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                            <Avatar
                                src={position && typeof position !== 'boolean' ? position.avatar : ''}
                                sx={{
                                    width: 42,
                                    height: 42,
                                    mb: 0.5,
                                    bgcolor: position ? 'primary.main' : 'grey.700'
                                }}
                            >
                                {!position && '?'}
                            </Avatar>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: palette.blue,
                                    textAlign: 'center',
                                    fontSize: 15
                                }}
                            >
                                {position && typeof position !== 'boolean' ? position.login : 'Пусто'}
                            </Typography>
                        </Box>
                    ))}

                    {server.positions.length < 3 ? (
                        <>
                            {Array.from({ length: 3 - server.positions.length }, (_, index) => (
                                <Box key={`empty-${index}`} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                                    <IconButton sx={{ mb: 1, p: isMobile ? 0.5 : 1 }}>
                                        <RocketLaunchIcon sx={{ color: 'grey.400', fontSize: isMobile ? 16 : 20 }} />
                                    </IconButton>
                                    <Avatar
                                        sx={{
                                            width: isMobile ? 24 : 32,
                                            height: isMobile ? 24 : 32,
                                            mb: 0.5,
                                            bgcolor: 'grey.700'
                                        }}
                                    >
                                        ?
                                    </Avatar>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: 'grey.500',
                                            textAlign: 'center',
                                            fontSize: isMobile ? '0.6rem' : '0.7rem'
                                        }}
                                    >
                                        Пусто
                                    </Typography>
                                </Box>
                            ))}
                        </>
                    ) : null}
                </Box>

                <Button
                    variant="contained"
                    fullWidth
                    component="a"
                    href={server.link}
                    target="_blank"
                    sx={{
                        bgcolor: 'rgba(138, 118, 200, 0.8)',
                        color: 'white',
                        textTransform: 'none',
                        borderRadius: 1,
                        fontSize: isMobile ? '0.8rem' : '0.875rem',
                        py: isMobile ? 0.75 : 1,
                        '&:hover': {
                            bgcolor: 'rgba(138, 118, 200, 1)',
                        }
                    }}
                >
                    Перейти к столу
                </Button>
            </CardContent>
        </Card>
    );

    const currentProgram = filteredPrograms[selectedProgramTab];

    const CurrencyTab = ({ currency, label }: { currency: CurrencyType; label: string }) => (
        <button
            onClick={() => handleCurrencyChange(currency)}
            className={selectedCurrency === currency ? "contained-button" : "outlined-button"}
            style={{
                cursor: 'pointer',
                backgroundColor: selectedCurrency === currency ? palette.blue : '#fff',
                borderRadius: '6px',
                padding: isMobile ? "6px 12px" : "8px 20px",
                fontSize: isMobile ? '14px' : '16px',
                color: selectedCurrency === currency ? '#fff' : '#041ee9',
                fontWeight: 'bold',
                border: '1px solid transparent',
                borderColor: selectedCurrency === currency ? palette.blue : '#041ee930',
            }}
        >
            {label}
        </button>
    );

    return (
        <PageContainer title={`${REACT_APP_NAME} | Анализ цепочек`} description="Анализ цепочек">
            <>
                <Box mb={2} display='flex' alignItems='center' flexWrap='wrap' justifyContent='space-between' gap={2}>
                    <Box>
                        <Typography variant='h4'>Анализ цепочек</Typography>
                        <Typography variant="body2" sx={{ color: 'grey.400' }}>
                            Главная » Структура » Анализ цепочек
                        </Typography>
                    </Box>
                    <Box sx={{ minWidth: isMobile ? '100%' : '250px' }}>
                        <TextField
                            fullWidth
                            size="small"
                            value={valueLogin}
                            onChange={(e) => setValueLogin(e.target.value)}
                            placeholder="Поиск"
                            onKeyDown={handleKeyDown}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton sx={{ background: "white" }} onClick={handleSubmit} edge="end">
                                            <IconSearch />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                </Box>

                {/* Валютные вкладки */}
                <Typography variant='h6' sx={{ color: 'text.secondary', fontWeight: 400, mb: 1 }}>
                    Валюта
                </Typography>
                <Box mb={2}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <CurrencyTab currency="usdt" label="USDT" />
                        <CurrencyTab currency="btc" label="Bitcoin (BY)" />
                    </Box>
                </Box>

                <Typography variant='h6' sx={{ color: 'text.secondary', fontWeight: 400 }}>
                    Платформы
                </Typography>

                <Box mb={2}>
                    {isLoading ? (
                        <Box display="flex" flexWrap="wrap" gap={1}>
                            {[...Array(8)].map((_, index) => (
                                <Skeleton
                                    key={index}
                                    variant="rounded"
                                    width={80}
                                    height={42}
                                    sx={{
                                        borderRadius: '12px',
                                        animation: 'pulse 1.5s ease-in-out infinite'
                                    }}
                                />
                            ))}
                        </Box>
                    ) : filteredPrograms.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, width: '100%' }}>
                            {filteredPrograms.map((program, index) => (
                                <button
                                    key={program.id}
                                    onClick={() => handleProgramTabChange(null, index)}
                                    className={selectedProgramTab === index ? "contained-button" : "outlined-button"}
                                    style={{
                                        cursor: 'pointer',
                                        backgroundColor: selectedProgramTab === index ? palette.blue : '#fff',
                                        borderRadius: '6px',
                                        padding: "8px 16px",
                                        fontSize: isMobile ? '14px' : '16px',
                                        color: selectedProgramTab === index ? '#fff' : '#041ee9',
                                        fontWeight: 'bold',
                                        border: '1px solid transparent',
                                        borderColor: selectedProgramTab === index ? palette.blue : '#041ee930',
                                    }}
                                    dangerouslySetInnerHTML={{ __html: program.name }}
                                />
                            ))}
                        </Box>
                    ) : (
                        <Typography sx={{ color: 'grey.400' }}>
                            Нет программ для выбранной валюты
                        </Typography>
                    )}
                </Box>

                {/* Уровни */}
                {currentProgram?.levels && currentProgram.levels.length > 0 && (
                    <Box>
                        <Typography variant='h6' sx={{ color: 'text.secondary', fontWeight: 400 }}>
                            Уровни
                        </Typography>
                        {isLoading ? (
                            <Box display='flex' mb={2} gap={1}>
                                {[...Array(3)].map((_, index) => (
                                    <Skeleton
                                        key={index}
                                        variant="rectangular"
                                        width={80}
                                        height={38}
                                        sx={{ borderRadius: '12px', animation: 'pulse 1.5s ease-in-out infinite' }}
                                    />
                                ))}
                            </Box>
                        ) : (
                            <Box mb={2}>
                                {/* <pre>
                                    {JSON.stringify(currentProgram?.currency, null, 2)}
                                </pre> */}
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, width: '100%' }}>
                                    {currentProgram.levels.map((level, index) => (
                                        <button
                                            key={index}
                                            onClick={() => handleLevelTabChange(null, index)}
                                            className={selectedLevelTab === index ? "contained-button" : "outlined-button"}
                                            style={{
                                                cursor: 'pointer',
                                                backgroundColor: selectedLevelTab === index ? palette.blue : '#fff',
                                                borderRadius: '6px',
                                                padding: isMobile ? "6px 12px" : "8px 16px",
                                                fontSize: isMobile ? '12px' : '14px',
                                                color: selectedLevelTab === index ? '#fff' : '#041ee9',
                                                fontWeight: 'bold',
                                                border: '1px solid transparent',
                                                borderColor: selectedLevelTab === index ? palette.blue : '#041ee930',
                                            }}
                                            dangerouslySetInnerHTML={{
                                                __html: `${level.name} (${level?.level || 0}) ${currentProgram?.currency?.toLocaleLowerCase() == 'btc' ? 'BY' : '$'}`
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Карточки серверов */}
                {isLoading ? (
                    <Grid container spacing={isMobile ? 1 : 2}>
                        {[...Array(8)].map((_, index) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                                <Box sx={{
                                    bgcolor: '#281c5263',
                                    border: '1px solid rgba(138, 118, 200, 0.3)',
                                    borderRadius: 2,
                                    minHeight: 280,
                                    p: 2,
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}>
                                    <Skeleton
                                        variant="text"
                                        height={32}
                                        sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', mb: 2, animation: 'pulse 1.5s ease-in-out infinite' }}
                                    />
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                                        <Skeleton
                                            variant="circular"
                                            width={60}
                                            height={60}
                                            sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', mb: 1, animation: 'pulse 1.5s ease-in-out infinite' }}
                                        />
                                        <Skeleton
                                            variant="text"
                                            width={100}
                                            sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', animation: 'pulse 1.5s ease-in-out infinite' }}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 'auto', mb: 2 }}>
                                        {[...Array(2)].map((_, idx) => (
                                            <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                                                <Skeleton
                                                    variant="circular"
                                                    width={20}
                                                    height={20}
                                                    sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', mb: 1, animation: 'pulse 1.5s ease-in-out infinite' }}
                                                />
                                                <Skeleton
                                                    variant="circular"
                                                    width={32}
                                                    height={32}
                                                    sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', mb: 0.5, animation: 'pulse 1.5s ease-in-out infinite' }}
                                                />
                                                <Skeleton
                                                    variant="text"
                                                    width={40}
                                                    sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', animation: 'pulse 1.5s ease-in-out infinite' }}
                                                />
                                            </Box>
                                        ))}
                                    </Box>
                                    <Skeleton
                                        variant="rectangular"
                                        height={36}
                                        sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: 1, animation: 'pulse 1.5s ease-in-out infinite' }}
                                    />
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                ) : error ? (
                    <Box textAlign="center" py={4}>
                        <Typography color="error">
                            Ошибка загрузки данных. Попробуйте обновить страницу.
                        </Typography>
                    </Box>
                ) : transformedServerCards.length > 0 ? (
                    <Grid container spacing={isMobile ? 1 : 2}>
                        {transformedServerCards.map((server) => (
                            <Grid sx={{ display: 'flex' }} item xs={12} sm={6} md={4} xl={3} key={server.id}>
                                <ServerCard server={server} />
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <Box textAlign="center" py={4}>
                        <Typography variant='h5' sx={{ color: 'grey.400' }}>
                            {/* Нет данных для отображения */}
                            {serverMessage}
                        </Typography>
                    </Box>
                )}
            </>
        </PageContainer>
    );
};